import { publicConfig } from "@lululemon/ecom-core";
import { createEncodedKey } from "@lululemon/ecom-common/src/util/helpers";
import * as atgApi from "@lululemon/ecom-common/src/services/atg";
import {
  getReviewsGQL,
  postComment,
  postContentPositiveFeedback,
  postReview as submitReview,
} from "../graphql/bazaarvoice";
import { reviewsLogger, ERRORS_FINGERPRINTS } from "../utils/reviews-logger";

// CONSTANTS
export const DEFAULT_OFFSET = 0;
export const DEFAULT_LIMIT = 16;
export const DEFAULT_SORT = "Rating:desc";
export const POSITIVE_VOTE = "Positive";
export const NEGATIVE_VOTE = "Negative";
export const REVIEW_CONTENT_TYPE = "review";
export const REVIEW_COMMENT_CONTENT_TYPE = "review_comment";
const REVIEWS_FEEDBACK_KEY = "reviewsFeedback";
export const INCENTIVES_SECTION = "incentives";
export const PHOTOS_SECTION_KEY = "photos";
export const RATING_SECTION_KEY = "rating";
export const SIZES_SECTION = "size";
export const FIT_SCALE_CONFIG = {
  minAttribute: { value: 1, label: "Small" },
  midAttribute: { value: 3, label: "True to size" },
  maxAttribute: { value: 5, label: "Large" },
};

/**
 * Maps locale to bazaarvoice supported locale.
 * List of Bazaarvoice supported locales go to
 * https://developer.bazaarvoice.com/conversations-api/tutorials/internationalization#supported-locales
 * @param {string} locale
 * @returns {string} bazaarvoice supported locale
 */
export const mapLocale = (locale = "en_US") =>
  locale?.toLowerCase().startsWith("fr") ? "fr_CA" : "en_US";

export const getReviewCutoffDate = () => {
  // We only want to retrieve reviews that were posted during or after 2018
  // This will give us reviews that are more accurate to today's products
  const cutoffDateMs = new Date(2018, 0).getTime(); // Returns Jan 1, 2018 in ms
  return cutoffDateMs / 1000; // Convert to seconds
};

export const DEFAULT_FILTER_STATE = {
  [`${RATING_SECTION_KEY}`]: [
    {
      isSelected: false,
      label: "5 stars",
      value: 5,
    },
    {
      isSelected: false,
      label: "4 stars",
      value: 4,
    },
    {
      isSelected: false,
      label: "3 stars",
      value: 3,
    },
    {
      isSelected: false,
      label: "2 stars",
      value: 2,
    },
    {
      isSelected: false,
      label: "1 star",
      value: 1,
    },
  ],
  [`${PHOTOS_SECTION_KEY}`]: [
    {
      isSelected: false,
      label: "Reviews with images only",
      value: "photos",
    },
  ],
  [`${INCENTIVES_SECTION}`]: [
    {
      isSelected: false,
      label: "Non-incentivized reviews",
      value: "incentives",
    },
  ],
  [`${SIZES_SECTION}`]: [],
};

/**
 * Formats the active filters in filterState to be used in the Bazaarvoice querystring
 * @param {Object} filterState
 * @returns {array}
 */
export const filtersToQueryParams = (filters = {}) =>
  Object.entries(filters).reduce((accFilters, [sectionKey, filterSection]) => {
    const activeFilters = filterSection
      .filter((f) => f.isSelected)
      .map((activeFilter) => activeFilter.value);

    if (activeFilters.length) {
      switch (sectionKey) {
        case INCENTIVES_SECTION: {
          return [
            ...accFilters,
            "AdditionalField_IncentivizedReview_1:neq:true",
            "ContextDataValue_IncentivizedReview_1:neq:true",
            "ContextDataValue_IncentivizedReview:neq:true",
            "ContextDataValue_Giveaway:neq:true",
            "ContextDataValue_isSweatCollectiveMember:neq:true",
          ];
        }
        case PHOTOS_SECTION_KEY:
        case RATING_SECTION_KEY: {
          const bvFilterField =
            sectionKey === RATING_SECTION_KEY ? "Rating" : "HasPhotos";
          const filterString =
            activeFilters.toString() === "photos" || activeFilters.toString();
          return [...accFilters, `${bvFilterField}:eq:${filterString}`];
        }
        case SIZES_SECTION: {
          return [
            ...accFilters,
            `Additionalfield_WhatSizeDidYouPurchase:eq:${activeFilters.join()}`,
          ];
        }
      }
    }

    return accFilters;
  }, []);

/**
 * @param {String} dataType
 * @param {Object} filters
 * @returns {string}
 */
export const getBazaarVoiceUrl = (dataType, filters = {}) => {
  const {
    url: baseUrl = "",
    apiVersion = "",
    apiKey = "",
  } = publicConfig?.bazaarVoice || {};
  const mergedFilters = {
    passkey: apiKey,
    ApiVersion: apiVersion,
    ...filters,
  };
  const queryString = Object.keys(mergedFilters)
    .map((key) => {
      if (key === "filter") {
        // Query string can have multiple 'filters' - join them together too
        return mergedFilters[key]
          .map((filterKey) => `${key}=${encodeURIComponent(filterKey)}`)
          .join("&");
      }
      return `${key}=${encodeURIComponent(mergedFilters[key])}`;
    })
    .join("&");
  return `${baseUrl}/data/${dataType}.json?${queryString}`;
};

/**
 * @param {String} locale
 * @param {String} productNameId
 * @param {Int} offset
 * @param {String} losearchcale
 * @param {Array} rating
 * @param {Array} filters
 */
export const getReviews = async ({
  client,
  locale,
  productNameId,
  offset = 0,
  search = "",
  rating = [],
  filters = [],
  sort = "",
}) => {
  if (!productNameId) {
    throw Error("Error Getting Reviews - No Product Id");
  }

  const gQLResponse = await getReviewsGQL({
    client,
    locale: mapLocale(locale),
    productNameId,
    offset,
    search,
    rating,
    filters,
    sort,
  });
  return {
    results: {
      ...gQLResponse?.getReviews,
      data: gQLResponse?.getReviews?.results,
    },
    reviewProductStatistics: gQLResponse?.getReviews?.stats,
  };
};

/**
 * @param {graphqlClient} client
 * @param {String} bvProductId
 * @param {Object} steps
 * @param {Object} steps.reviewState
 * @param {String} steps.reviewState.rating
 * @param {String} steps.reviewState.title
 * @param {String} steps.reviewState.review
 * @param {Object} steps.infoState
 * @param {String} steps.infoState.email
 * @param {String} steps.infoState.badge
 * @param {String} steps.infoState.nickname
 * @param {Object} steps.sizeAndFitState
 * @param {String} [steps.sizeAndFitState.contextdatavalue_Activity]
 * @param {String} [steps.sizeAndFitState.contextdatavalue_BodyType]
 * @param {Boolean} [steps.sizeAndFitState.isSweatCollective]
 * @param {String} [steps.sizeAndFitState.additionalfield_WhatIsYourUsualSize]
 * @param {String} [steps.sizeAndFitState.additionalfield_WhatSizeDidYouPurchase]
 * @param {Object} steps.photoState
 * @param {Object} [steps.photoState.photos]
 * @param {String} deviceFingerprint
 * @param {String} pdpUrl
 * @param {String} locale
 */
export const postReview = async ({
  client,
  bvProductId,
  steps,
  fingerprint,
  pdpUrl,
  locale,
}) => {
  if (!bvProductId) {
    throw Error("Error Getting Reviews - No Product Id");
  }

  const { reviewState, infoState, sizeAndFitState, photoState } = steps;
  const { rating, title, review } = reviewState;
  const {
    badge,
    incentivizedReview,
    email,
    nickname,
    sendNotifications,
    allowGiveaway,
  } = infoState;
  const hostName = `https://${atgApi.getHostName()}`;
  const formatPhotos = (photos = {}) => {
    if (!photos || Object.keys(photos).length === 0) {
      return;
    }

    const {
      photoCaption_1,
      photoUrl_1,
      photoCaption_2,
      photoUrl_2,
      photoCaption_3,
      photoUrl_3,
      photoCaption_4,
      photoUrl_4,
      photoCaption_5,
      photoUrl_5,
      photoCaption_6,
      photoUrl_6,
    } = photos;

    return [
      [photoUrl_1, photoCaption_1],
      [photoUrl_2, photoCaption_2],
      [photoUrl_3, photoCaption_3],
      [photoUrl_4, photoCaption_4],
      [photoUrl_5, photoCaption_5],
      [photoUrl_6, photoCaption_6],
    ].filter(([caption, url]) => caption && url);
  };

  try {
    return await submitReview({
      activity: sizeAndFitState?.contextdatavalue_Activity,
      allowGiveaway,
      badge,
      bodyType: sizeAndFitState?.contextdatavalue_BodyType,
      callbackUrl: `${hostName}${pdpUrl}`,
      client,
      deviceFingerprint: fingerprint,
      email: email,
      locale: mapLocale(locale),
      isIncentivizedReview: incentivizedReview === "true",
      isSweatCollective: sizeAndFitState?.isSweatCollectiveMember,
      nickname,
      photos: formatPhotos(photoState?.photos),
      productNameId: bvProductId,
      ratingFit: sizeAndFitState?.rating_Fit_22,
      rating: `${rating}`,
      sendEmailAlertWhenCommented: sendNotifications,
      sendEmailAlertWhenPublished: true,
      text: review?.userInput,
      title: title,
      whatIsYourUsualSize: sizeAndFitState?.additionalfield_WhatIsYourUsualSize,
      whatSizeDidYouPurchase:
        sizeAndFitState?.additionalfield_WhatSizeDidYouPurchase,
    });
  } catch (error) {
    reviewsLogger({
      bazaarvoiceId: bvProductId,
      error,
      componentName: "ecom-pdp/src/reviews/index",
      functionName: "postReview",
      message: ERRORS_FINGERPRINTS.POSTING_CONTENT,
      pdpUrl,
    });
  }
};

export const setHasSubmittedReviewsPreviously = () => {
  if (typeof localStorage !== "undefined") {
    const id = window.localStorage.getItem("reviewsFeedbackUserId");
    if (id) {
      localStorage.setItem("hasSubmittedReviewsPreviously", "true");
    } else {
      throw Error(
        "Error Setting hasSubmittedReviewsPreviously - No reviewsFeedbackUserId in Storage"
      );
    }
  }
};

/**
 * @param {graphqlClient} client
 * @param {Object} commentData
 * @param {Object} commentData.reviewId
 * @param {Object} commentData.commentText
 * @param {Object} commentData.userNickname
 * @param {Object} commentData.userEmail
 * @param {Object} commentData.pdpUrl
 * @param {string} locale
 */
export const postReviewComment = async ({
  client,
  commentData = {},
  locale,
}) => {
  const {
    reviewId = "",
    commentText = "",
    userNickname = "",
    userEmail = "",
    pdpUrl: hostedAuthenticationCallbackUrl = "",
  } = commentData;

  try {
    const user = localStorage.getItem("bazaarvoice_uas") || "";
    const auth = user
      ? {
          user,
        }
      : {
          hostedAuthenticationCallbackUrl,
          userEmail,
          userNickname,
        };

    return await postComment({
      client,
      commentText,
      locale: mapLocale(locale),
      reviewId,
      ...auth,
    });
  } catch (error) {
    reviewsLogger({
      bazaarvoiceId: "",
      error: error,
      componentName: "ecom-pdp/src/reviews/index",
      context: {},
      functionName: "postReviewCommentNew",
      message: ERRORS_FINGERPRINTS.POSTING_CONTENT,
      pdpUrl: hostedAuthenticationCallbackUrl,
      tags: {
        loadFromKiwi: true,
        loadFromBazaarVoice: false,
        reviewId: reviewId,
      },
    });
  }
};

/**
 * @param bvResponse
 */
export const validateBVCommentAndReviewsResponse = (bvResponse) => {
  if (!bvResponse || !bvResponse?.Results) {
    throw Error("Invalid BV response Data.");
  }
  const { HasErrors, Errors } = bvResponse;

  if (HasErrors) {
    const errors = Errors.map(({ Message }) => Message);
    throw Error(errors.toString());
  }
};

/**
 * @param {{Products: null}} reviewData bazar voice Includes list
 * @param {Object} reviewData.Products
 * @param {object} reviewData.ProductsOrder
 * see https://stg.api.bazaarvoice.com/data/reviews.json?apiversion=5.4&passkey=caB45h2jBqXFw1OE043qoMBD1gJC8EwFNCjktzgwncXY4&Filter=ProductId:data-gen-moppq9ekthfzbc6qff3bqokie&Include=Products&Stats=Reviews
 * @returns {{}|{ratingDistribution: *, averageOverallRating: *}}
 */
export const getReviewsStats = (reviewData) => {
  if (!reviewData?.Products || !reviewData?.ProductsOrder) {
    return {};
  }
  const productId = reviewData?.ProductsOrder?.[0];
  const productStatistics =
    reviewData?.Products?.[productId]?.FilteredReviewStatistics;
  //Fit_22 is the identifier for the fit Rating component in BazaarVoice
  const secondaryRating = productStatistics?.SecondaryRatingsAverages?.Fit_22;
  const features = [];
  for (const key in productStatistics?.SecondaryRatingsAverages) {
    if (productStatistics?.SecondaryRatingsAverages[key].Id != "Fit_22") {
      features.push({
        featureId: productStatistics?.SecondaryRatingsAverages[key].Id,
        averageRating:
          productStatistics?.SecondaryRatingsAverages[key].AverageRating,
      });
    }
  }
  return {
    averageOverallRating: productStatistics?.AverageOverallRating,
    totalReviewCount: productStatistics?.TotalReviewCount,
    ratingDistribution: productStatistics?.RatingDistribution.map((res) => ({
      ratingValue: res?.RatingValue,
      numberOfResults: res?.Count,
    })),
    fitsRating: {
      range: secondaryRating?.ValueRange,
      rating: secondaryRating?.AverageRating,
    },
    features,
  };
};

/**
 * @param {Object} reviewsData Bazaar Voice reviews Data.
 * see https://developer.bazaarvoice.com/conversations-api/reference/v5.4/reviews/review-display#response-format
 */
export const combineReviewPhotos = (reviewsData) => {
  try {
    if (!reviewsData || !reviewsData?.data) {
      throw Error("Invalid reviews Data.");
    }

    const allPhotos = [];
    reviewsData.data.map((review) => {
      review.photos.map((photo) => {
        allPhotos.push({ ...photo, reviewId: review.id });
      });
    });
    return allPhotos;
  } catch (error) {
    reviewsLogger({
      error,
      componentName: "review-photo-step",
      functionName: "combineReviewPhotos",
      message: ERRORS_FINGERPRINTS.UPLOADING_IMAGE,
    });
    throw error;
  }
};

/**
 *
 * @param {array} arr selectedFiles from the explorer, formated into BV required object
 * see https://developer.bazaarvoice.com/conversations-api/reference/v5.4/media/photo-upload
 * @returns {Object}
 */
export const formatBVPhotos = (arr) => {
  if (arr.length === 0) {
    return null;
  }
  if (!Array.isArray(arr)) {
    throw Error("formatBVPhotos argument is not an array");
  }
  const formatedPhotos = {};
  arr.forEach((item, index) => {
    formatedPhotos[`photoUrl_${index + 1}`] = item.thumbnail;
    formatedPhotos[`photoCaption_${index + 1}`] = item.name;
  });
  return formatedPhotos;
};

/**
 *
 * @param {selectedFile} file selectedFile from the explorer
 * @param {String} contentType Review || Question || Answer
 * see https://developer.bazaarvoice.com/conversations-api/reference/v5.4/media/photo-upload
 * @returns {Object}
 */
export const uploadImageToBV = async (file, contentType, locale) => {
  const formData = new FormData();
  formData.append("photo", file, file?.name);

  const bazaarvoiceLocale = mapLocale(locale);
  const apiVersion = publicConfig?.bazaarVoice?.apiVersion;

  const requestOptions = {
    method: "POST",
    body: formData,
    redirect: "follow",
    "Content-Type": "form-data",
  };

  const url = `${publicConfig?.bazaarVoice?.uploadPhotoUrl}?ApiVersion=${apiVersion}&ContentType=${contentType}&Locale=${bazaarvoiceLocale}`;

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const json = await response.json();
    if (json.HasErrors) {
      return [
        {
          hasErrors: true,
          errCode: json.FormErrors.FieldErrors.photo.Code,
        },
      ];
    }
    return formatBazaarvoicePhotos([json?.Photo]);
  } catch (error) {
    reviewsLogger({
      error,
      componentName: "review-photo-step",
      functionName: "uploadImageToBV",
      message: ERRORS_FINGERPRINTS.UPLOADING_IMAGE,
    });
  }
};

/**
 *
 * @param {selectedFile} file selectedFile from the explorer
 * @returns {Boolean}
 */
export const isFileBrowserSupported = (file) =>
  file.name.toLowerCase().includes(".tiff") ||
  file.name.toLowerCase().includes(".heic");

export const formatBazaarvoicePhotos = (photos) =>
  photos.map((photo) => ({
    caption: photo?.Caption,
    id: photo?.Id,
    sizes: {
      normal: {
        id: photo?.Sizes?.normal?.Id,
        url: photo?.Sizes?.normal?.Url,
      },
      thumbnail: {
        id: photo?.Sizes?.thumbnail?.Id,
        url: photo?.Sizes?.thumbnail?.Url,
      },
    },
    sizesOrder: photo?.SizesOrder,
  }));

/**
 * see https://developer.bazaarvoice.com/conversations-api/reference/v5.4-prr/reviews/review-display#response-format for example of reviewResponseData
 * @returns {*[]}
 * @param reviewResponseData [*{}]
 */
export const formatReviewResponses = (reviewResponseData) => {
  if (!reviewResponseData || !reviewResponseData?.length) {
    return [];
  }

  // Sort by date (oldest to newest)
  const sortedResponseData = reviewResponseData?.sort((a, b) => {
    if (!a?.Date || !b?.Date) {
      throw Error("Error Getting Sorting GEC Responses - Invalid Date");
    }

    const dateA = new Date(a?.Date);
    const dateB = new Date(b?.Date);
    return dateA - dateB;
  });

  const formattedResponse = sortedResponseData?.map((response) => ({
    responseText: response?.Response,
    nickName: "Guest Education Centre (GEC)",
    submissionTime: response?.Date,
  }));
  return formattedResponse || [];
};

/**
 * @param {Object} reviewsData Bazaar Voice reviews Data.
 * @deprecated we should remove this once the reviews photo carousel AB test feature is migrated/removed
 */
export const formatReviewsData = (reviewsData) => {
  try {
    validateBVCommentAndReviewsResponse(reviewsData);
    const { Results = [], TotalResults, Offset, Limit, Includes } = reviewsData;
    const reviewProductStatistics = getReviewsStats(Includes);
    const results = {
      totalResults: TotalResults,
      offset: Offset,
      limit: Limit,
    };
    results.data = Results.map((res) => ({
      dislikes: res?.TotalNegativeFeedbackCount,
      id: res?.Id,
      badge: res?.badge,
      isVerifiedBuyer: res?.Badges?.VerifiedPurchaser?.Id,
      fitInformation: {
        fits: res?.SecondaryRatings?.Fit_22?.ValueLabel,
        WhatIsYourUsualSize: res?.AdditionalFields?.WhatIsYourUsualSize?.Value,
        sizePurchased: res?.AdditionalFields?.WhatSizeDidYouPurchase?.Value,
        height: null,
        weight: null,
      },
      likes: res?.TotalPositiveFeedbackCount,
      nickName: res?.UserNickname,
      photos: formatBazaarvoicePhotos(res?.Photos),
      rating: res?.Rating,
      ratingRange: res?.RatingRange,
      responses: formatReviewResponses(res?.ClientResponses),
      reviewText: res?.ReviewText,
      submissionTime: res?.SubmissionTime,
      title: res?.Title,
      totalComments: res?.TotalCommentCount,
    }));

    return { results, reviewProductStatistics };
  } catch (error) {
    reviewsLogger({
      error,
      componentName: "ReviewsPhotoCarouselContainer",
      functionName: "formatReviewsData",
      message: ERRORS_FINGERPRINTS.LOADING_REVIEWS,
    });
    throw error;
  }
};

export const validateFeedBackData = (
  feedbackData,
  isBazaarvoiceEndpoint = true
) => {
  const FEEDBACK_VOTES = [POSITIVE_VOTE, NEGATIVE_VOTE];
  if (!feedbackData?.contentId) {
    throw Error("Error Posting Feedback - No Content Id");
  }
  if (!feedbackData?.contentType) {
    throw Error("Error Posting Feedback - No Content Type");
  }
  if (!feedbackData?.vote && isBazaarvoiceEndpoint) {
    throw Error("Error Posting Feedback - No Vote Type");
  }
  if (
    feedbackData?.vote &&
    !FEEDBACK_VOTES.includes(feedbackData?.vote) &&
    isBazaarvoiceEndpoint
  ) {
    throw Error("Error Posting Feedback - Invalid Vote Type entered");
  }
};

/**
 * @returns {string}
 */
export const getFeedbackUserId = () => {
  const uniqueId = Date.now();
  const randomUserId = createEncodedKey(uniqueId);
  if (typeof localStorage !== "undefined") {
    const userId = localStorage.getItem("reviewsFeedbackUserId");
    return userId || randomUserId;
  }
  return randomUserId;
};

/**
 * Post positive feedback using Kiwi
 * @param {graphqlClient} client: urql graphql client
 * @param {string} contentId
 * @param {string} contentType: type of Bazaarvoice content
 * @param {string} locale: Bazaarvoice content locale
 * @returns {Object} Bazaarvoice feedback submission response
 */
export const postPositiveFeedback = async ({
  client,
  contentId,
  contentType,
  locale,
}) => {
  const userAuthString = localStorage.getItem("bazaarvoice_uas");
  validateFeedBackData(
    {
      contentId,
      contentType,
    },
    false
  );

  const { postContentFeedback } = await postContentPositiveFeedback({
    client,
    contentId,
    contentType,
    locale: mapLocale(locale),
    user: userAuthString || "",
  });

  return postContentFeedback;
};

export const getLocalStorageFeedbacks = () => {
  if (typeof localStorage === "undefined") {
    return [];
  }
  const feedbacks = localStorage.getItem(REVIEWS_FEEDBACK_KEY);
  return JSON.parse(feedbacks || "[]");
};

/**
 *
 * @param feedbackData
 * @returns {void}
 */
export const storeFeedbacks = (feedbackData) => {
  if (typeof localStorage !== "undefined") {
    const { contentId, contentType } = feedbackData;
    const feedbacksArray = getLocalStorageFeedbacks();
    const feedbackExists = !!feedbacksArray.find(
      (feedback) =>
        feedback?.contentId === contentId &&
        feedback?.contentType === contentType
    );
    if (!feedbackExists) {
      feedbacksArray.push(feedbackData);
    }
    localStorage.setItem(REVIEWS_FEEDBACK_KEY, JSON.stringify(feedbacksArray));
  }
};

/**
 * @param contentId
 * @param contentType
 * @returns {boolean}
 */
export const hasLikedContent = (contentId, contentType) => {
  if (typeof localStorage === "undefined") {
    return false;
  }
  const feedbacks = getLocalStorageFeedbacks();

  const feedback = feedbacks.find(
    (feedback) =>
      feedback?.contentId === contentId && feedback?.contentType === contentType
  );
  return !!feedback;
};

/**
 * @param {boolean} [isUsingKiwi]
 * @returns {boolean}
 */
export const hasSubmittedReviewsPreviously = (isUsingKiwi = false) => {
  if (typeof localStorage !== "undefined") {
    if (isUsingKiwi) {
      return !!localStorage.getItem("bazaarvoice_uas");
    }
    return !!localStorage.getItem("hasSubmittedReviewsPreviously");
  }
  return false;
};

export const storeReviewUserIdLocally = (userId) => {
  if (userId && typeof localStorage !== "undefined") {
    localStorage.setItem("reviewsFeedbackUserId", userId);
  }
};

export const handleReviewCommentError = (reviewCommentErrors = {}) => {
  const errors = Object.entries(reviewCommentErrors).map(([, e]) => e.Message);
  const errorStr = errors.join("\n");
  throw Error(errorStr);
};

/**
 * Fetch reviews and review product statistics
 * @param {string} bvId bazaarvoice identifier
 * @param {graphQLClient} client
 * @param {string} pdpUrl
 * @param {string} sortBy
 * @param {string} locale
 * @param {string} componentName
 */
export const fetchReviews = async (
  bvId,
  client,
  pdpUrl,
  sortBy,
  locale,
  componentName
) => {
  const getReviewsEmptyResponse = {
    reviewsData: {
      results: { data: [], totalResults: 0, hasErrors: true },
      reviewProductStatistics: { averageOverallRating: 0, totalReviewCount: 0 },
    },
    bvProdId: null,
  };

  if (!bvId) {
    reviewsLogger({
      bazaarvoiceId: bvId,
      error: "No Bazaarvoice ID found for this product",
      componentName,
      functionName: "fetchReviews",
      message: ERRORS_FINGERPRINTS.LOADING_REVIEWS,
      pdpUrl,
      tags: {
        locale: mapLocale(locale),
      },
    });
    return getReviewsEmptyResponse;
  }

  try {
    const reviewsData = await getReviews({
      client,
      locale: mapLocale(locale),
      productNameId: bvId,
      sort: sortBy,
    });

    return { reviewsData, bvProdId: bvId };
  } catch (error) {
    reviewsLogger({
      bazaarvoiceId: bvId,
      error,
      componentName,
      functionName: "fetchReviews",
      message: ERRORS_FINGERPRINTS.LOADING_REVIEWS,
      pdpUrl,
      tags: {
        locale: mapLocale(locale),
      },
    });
    return getReviewsEmptyResponse;
  }
};
